<template>
  <div class="wa-phone" :class="{ 'wa-phone--compact': compact }">
    <label v-if="!compact" class="wa-phone__label">WhatsApp</label>
    <div class="wa-phone__row">
      <select
        class="wa-phone__country"
        :value="selectedIso"
        @change="onCountryChange($event.target.value)"
        aria-label="País do telefone"
      >
        <option v-for="c in countries" :key="c.iso" :value="c.iso">
          {{ compact ? `${c.flag} +${c.dial}` : `${c.flag} +${c.dial} ${c.name}` }}
        </option>
      </select>
      <input
        type="tel"
        class="wa-phone__input"
        inputmode="numeric"
        autocomplete="tel-national"
        :placeholder="compact ? 'WhatsApp' : currentPlaceholder"
        :maxlength="currentMaxLength"
        :value="displayPhone"
        @input="onPhoneInput($event.target.value)"
      />
    </div>
    <p v-if="!compact" class="wa-phone__hint">Inclua o DDD. Usamos o WhatsApp para atendimento personalizado.</p>
  </div>
</template>

<script>
import { checkoutLog } from '../utils/checkoutDebug';

const COUNTRIES = [
  { iso: 'BR', name: 'Brasil', dial: '55', flag: '🇧🇷', placeholder: '(11) 99999-9999', maxLength: 11 },
  { iso: 'US', name: 'EUA', dial: '1', flag: '🇺🇸', placeholder: '(201) 555-0123', maxLength: 10 },
  { iso: 'PT', name: 'Portugal', dial: '351', flag: '🇵🇹', placeholder: '912 345 678', maxLength: 9 },
  { iso: 'AR', name: 'Argentina', dial: '54', flag: '🇦🇷', placeholder: '11 2345-6789', maxLength: 10 },
  { iso: 'MX', name: 'México', dial: '52', flag: '🇲🇽', placeholder: '55 1234 5678', maxLength: 10 },
  { iso: 'CO', name: 'Colômbia', dial: '57', flag: '🇨🇴', placeholder: '300 123 4567', maxLength: 10 },
  { iso: 'CL', name: 'Chile', dial: '56', flag: '🇨🇱', placeholder: '9 1234 5678', maxLength: 9 },
  { iso: 'PE', name: 'Peru', dial: '51', flag: '🇵🇪', placeholder: '912 345 678', maxLength: 9 },
  { iso: 'UY', name: 'Uruguai', dial: '598', flag: '🇺🇾', placeholder: '9123 4567', maxLength: 8 },
  { iso: 'PY', name: 'Paraguai', dial: '595', flag: '🇵🇾', placeholder: '981 123456', maxLength: 9 },
  { iso: 'BO', name: 'Bolívia', dial: '591', flag: '🇧🇴', placeholder: '7 123 4567', maxLength: 8 },
  { iso: 'VE', name: 'Venezuela', dial: '58', flag: '🇻🇪', placeholder: '412 123 4567', maxLength: 10 },
  { iso: 'ES', name: 'Espanha', dial: '34', flag: '🇪🇸', placeholder: '612 34 56 78', maxLength: 9 },
  { iso: 'IT', name: 'Itália', dial: '39', flag: '🇮🇹', placeholder: '312 345 6789', maxLength: 10 },
  { iso: 'DE', name: 'Alemanha', dial: '49', flag: '🇩🇪', placeholder: '1512 3456789', maxLength: 11 },
  { iso: 'FR', name: 'França', dial: '33', flag: '🇫🇷', placeholder: '6 12 34 56 78', maxLength: 9 },
  { iso: 'GB', name: 'Reino Unido', dial: '44', flag: '🇬🇧', placeholder: '7400 123456', maxLength: 10 },
  { iso: 'AO', name: 'Angola', dial: '244', flag: '🇦🇴', placeholder: '923 123 456', maxLength: 9 },
  { iso: 'MZ', name: 'Moçambique', dial: '258', flag: '🇲🇿', placeholder: '82 123 4567', maxLength: 9 }
];

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

export default {
  name: 'WhatsAppPhoneInput',
  props: {
    basket: {
      type: Object,
      required: true
    },
    compact: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      countries: COUNTRIES,
      selectedIso: 'BR',
      displayPhone: ''
    };
  },
  computed: {
    currentCountry() {
      return this.countries.find(c => c.iso === this.selectedIso) || this.countries[0];
    },
    currentPlaceholder() {
      return this.currentCountry.placeholder;
    },
    currentMaxLength() {
      return this.currentCountry.maxLength;
    }
  },
  mounted() {
    this.syncFromBasket();
    checkoutLog('form:whatsapp-input-mounted', { selectedIso: this.selectedIso }, this.$route);
    checkoutLog('form:whatsapp-input-ready', { displayPhone: this.displayPhone }, this.$route);
  },
  methods: {
    syncFromBasket() {
      const code = this.basket.telephone_country_code;
      if (code) {
        const match = this.countries.find(c => String(c.dial) === String(code));
        if (match) {
          this.selectedIso = match.iso;
        }
      } else if (!this.basket.telephone_country_code) {
        this.basket.telephone_country_code = this.currentCountry.dial;
      }

      const digits = digitsOnly(this.basket.telephone);
      this.displayPhone = digits;
    },
    onCountryChange(iso) {
      this.selectedIso = iso;
      this.displayPhone = '';
      this.basket.telephone = '';
      this.basket.telephone_country_code = this.currentCountry.dial;
    },
    onPhoneInput(value) {
      const digits = digitsOnly(value).slice(0, this.currentMaxLength);
      if (digits === this.displayPhone) {
        return;
      }
      this.displayPhone = digits;
      this.basket.telephone = digits;
    }
  }
};

</script>

<style lang="scss" scoped>
.wa-phone {
  margin-bottom: 0.75rem;

  &--compact {
    margin-bottom: 0;

    .wa-phone__country {
      max-width: 108px;
      min-width: 96px;
      padding: 0.55rem 0.4rem;
      font-size: 0.8rem;
    }

    .wa-phone__input {
      padding: 0.55rem 0.7rem;
      font-size: 0.9rem;
    }
  }

  &__label {
    display: block;
    margin-bottom: 0.35rem;
    font-size: 0.85rem;
    font-weight: 600;
    color: #5a5268;
  }

  &__row {
    display: flex;
    gap: 0.5rem;
    align-items: stretch;
  }

  &__country {
    flex: 0 0 auto;
    max-width: 42%;
    min-width: 118px;
    padding: 0.65rem 0.55rem;
    border: 1px solid #d8d0e4;
    border-radius: 10px;
    background: #fff;
    color: #2c2c2c;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    outline: none;

    &:focus {
      border-color: #5c3498;
      box-shadow: 0 0 0 2px rgba(92, 52, 152, 0.12);
    }
  }

  &__input {
    flex: 1 1 auto;
    min-width: 0;
    width: 100%;
    padding: 0.65rem 0.85rem;
    border: 1px solid #d8d0e4;
    border-radius: 10px;
    background: #fff;
    color: #2c2c2c;
    font-size: 0.95rem;
    outline: none;

    &:focus {
      border-color: #5c3498;
      box-shadow: 0 0 0 2px rgba(92, 52, 152, 0.12);
    }
  }

  &__hint {
    margin: 0.4rem 0 0;
    font-size: 0.8rem;
    color: #7a7288;
    line-height: 1.35;
  }
}
</style>
